/**
 * Order access — the buyer's own ticket surface, projected off the
 * OrderAccess contract (`packages/types/src/order-access.entity.ts`): "what
 * an email link may see without a session and without a function." This is
 * the SAME surface reached two ways — same-session, immediately after
 * payment (before `orderAccessToken` exists, backed by the buyer's own
 * `Order` listener), and cross-session, days later from the fulfillment
 * email (backed by `OrderAccess`, keyed by token, no session). The view
 * renders one projection and never knows which backing produced it.
 *
 * `status` mirrors `OrderStatusSchema` verbatim (authorised/paid/refunded/
 * cancelled) plus the two states no order status covers: "loading" and
 * "not-found" (the read resolved to nothing at all — a stale or malformed
 * link). The one deliberate addition beyond a pure entity mirror is
 * `fulfillment` on the "paid" branch: `Order.deliveriesComplete` names
 * "every delivery task done" as a single flag, but `OrderAccess` itself
 * carries no such signal, and a real order can sit at `orderStatus: "paid"`
 * — money captured, genuinely safe — for a real span of time before
 * `fulfillOrder`'s fan-out (tickets, PDF, receipt, .ics, wallet passes)
 * finishes minting anything. Money-safe and tickets-ready are two different
 * facts the buyer needs told apart, so "paid" splits into "issuing" (zero
 * tickets yet), "partial" (some tickets minted, more expected), and
 * "complete" — a named deviation from a pure schema mirror because the
 * schema doesn't yet carry the fact this view must render honestly.
 *
 * `expectedCount` is `null` whenever the total ticket count can't be
 * resolved from the active backing — OrderAccessSchema carries no line-item
 * breakdown, only the `tickets[]` minted so far, so a "3 of 5" progress
 * readout is unreachable until the contract gains a total-count field.
 * `artifacts` stays `null` until fulfillment is "complete": the PDF/.ics/
 * receipt bundle is order-wide, not per-ticket, so it isn't meaningfully
 * partial.
 *
 * Zero I/O. `order-access.sync.ts` (per app package — `web`'s reads
 * Order/OrderAccess, `designer`'s reads fixtures) is the sole writer,
 * resolving every Storage path (poster, artifacts) to a display URL before
 * it lands here, exactly as `events.state.ts`'s own `EventWithId.posterUrl`
 * already does.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import type { OrderAccess } from "@momo/types";
import { Subject } from "rxjs";

export type TicketFulfillment = "issuing" | "partial" | "complete";

/** The subset of firebase/firestore's Timestamp this view actually calls
 *  (.toDate(), to render) — a real Event.Event["schedule"] Timestamp always
 *  structurally satisfies this narrower shape, and so does a designer
 *  fixture's plain {toDate,toMillis} object (event-config.fixtures.ts's own
 *  wrapTimestamp escape hatch) with no firebase import and no zod parse
 *  boundary to route the looser shape through. */
export type TimestampLike = { toDate(): Date; toMillis(): number };

/**
 * One ticket as the buyer's page holds it — `OrderAccessSchema.tickets[]`'s
 * own element type, DERIVED rather than re-listed, so a field the contract
 * gains reaches this page by being added once instead of retyped here. The
 * fields are the contract's: ticketId, the buyer-facing typeName (Ticket
 * itself carries none; OrderAccess is the one place it is denormalized), the
 * per-ticket status, its own qrToken, the templateVariant
 * resolveTicketTemplateVariant froze onto it at fulfillment — the real
 * Editorial/Strata card order-access.view.ts mounts via @momo/ticket-template's
 * renderTicketVariantCard, never re-derived here — and the entryWindow the
 * handover sheet groups a large order by.
 *
 * `entryWindow` is the one field widened rather than taken: the contract types
 * it as a firebase `Timestamp` pair, and this projection is deliberately
 * reachable without that SDK (TimestampLike's own doc) so a designer fixture
 * satisfies it with a plain object. A real Timestamp satisfies the narrower
 * shape structurally, so the widening costs the page nothing and buys the
 * studio its fixtures.
 */
export type OrderAccessTicket = Readonly<
	Omit<OrderAccess.OrderAccess["tickets"][number], "entryWindow">
> &
	Pick<
		OrderAccess.OrderAccess["tickets"][number],
		"ticketId" | "templateVariant"
	> & {
		readonly entryWindow?:
			| { readonly opensAt: TimestampLike; readonly closesAt: TimestampLike }
			| null
			// Spelled out because this workspace runs exactOptionalPropertyTypes: an
			// absent key and an explicit undefined are two things here, and the
			// contract's own `.nullish()` produces both.
			| undefined;
	};

/** One artifact slot, resolved for display: the entity's own
 *  never/pending/stored union (order-access.entity.ts), with `stored`
 *  collapsed into `ready` carrying the fetchable URL it already resolves
 *  to, since the renderer never needs to know it was a Storage path.
 *  `never` and `pending` pass straight through unresolved — resolving
 *  either would be I/O this pure state can't perform anyway, and
 *  order-access.api.ts is where that resolution actually happens. */
export type ResolvedArtifactSlot =
	| { readonly state: "never" }
	| { readonly state: "pending" }
	| { readonly state: "ready"; readonly url: string };

/** The three answers every control keyed to an artifact renders differently:
 *  `never` reserves nothing (a control there would promise an action this
 *  order can never offer), `pending` reserves the control's own final
 *  footprint with a status affordance in it, `ready` carries the live
 *  control. A control that collapses `never` and `pending` into "render
 *  nothing" is what shoves the page around as each artifact lands. */
export type ArtifactSlotState = ResolvedArtifactSlot["state"];

/** OrderAccess.artifacts, resolved for display — order-access.api.ts
 *  resolves each Storage-backed slot's own path to a display URL before it
 *  lands here. Telling `never` (this order will never produce it) apart
 *  from `pending` (expected, not yet rendered) is the whole point: see
 *  artifactChips below, the one place that discrimination happens for the
 *  two slots (ics/receipt) that fit its single-href chip
 *  shape. walletGoogle carries the entity's own WalletGoogleSlot verbatim —
 *  there is nothing to resolve, since its `ready` state carries no payload
 *  and a save link is minted on demand instead — and is read directly by
 *  whatever renders the Google Wallet save badge; it does not participate
 *  in artifactChips. */
export type OrderAccessArtifacts = {
	/** The issued ticket files, verbatim from the entity — the client renders
	 *  the card itself, so these are never resolved to a display URL. */
	readonly ticketFiles: OrderAccess.TicketFilesSlot;
	readonly ics: ResolvedArtifactSlot;
	readonly receipt: ResolvedArtifactSlot;
	/** One {ticketId,url} pair per ticket's signed Apple Wallet pass,
	 *  addressed by identity, not array position — order-access.api.ts
	 *  resolves each stored Storage path to a bare alt=media URL before it
	 *  lands here, the same seam ics/receipt resolve through; empty for
	 *  `never`/`pending` alike. The per-ticket wallet control reads this: a
	 *  bundle cannot express one ticket's own pass. */
	readonly walletAppleUrls: readonly {
		readonly ticketId: string;
		readonly url: string;
	}[];
	/** The one URL that adds every pass in this order at once — the
	 *  `.pkpasses` bundle (a ZIP of the same signed `.pkpass` files, served as
	 *  application/vnd.apple.pkpasses) iOS 15+ adds in a single add, which is
	 *  what lets the order-level control be one control rather than one per
	 *  ticket. Null for `never`/`pending`, and null for an order fulfilled
	 *  before the bundle was written — those carry only their individual
	 *  passes, so their order-level control falls back to one per pass. */
	readonly walletAppleBundleUrl: string | null;
	/** Which of its three states the Apple Wallet slot is in — the state
	 *  token alone, since `stored`'s own paths already arrive resolved above.
	 *  The two url fields cannot answer this: both are empty for `never` and
	 *  `pending` alike, and a control that must hold its geometry has to tell
	 *  "this deployment signs no passes" (reserve nothing) from "the pass is
	 *  still being signed" (reserve the badge's own box). Optional because
	 *  each mounting app's own seam supplies it; absent reads as `never`, the
	 *  one reading that reserves nothing. */
	readonly walletApple?: OrderAccess.WalletAppleSlot["state"];
	readonly walletGoogle: OrderAccess.WalletGoogleSlot;
};

/**
 * The event/venue context this ticket belongs to — genuinely needed for
 * "beautiful cold, at 3am, on a stranger's phone" (the poster, the schedule,
 * the venue, the entrance notes). Mirrors OrderAccessSchema's own `event`/
 * `venue` snapshot (order-access.entity.ts), frozen at fulfillment time; the
 * in-session fallback (order-access.sync.ts's client-minted stand-in, before that doc exists)
 * projects the same shape from the buyer's own live event/location state
 * instead.
 */
export type OrderAccessEventContext = {
	readonly name: string;
	readonly posterUrl: string | null;
	/** Resolved display URL for the event's own uploaded wordmark
	 *  (`Event.logo`) — the ticket-card.view.ts's Stencil variant draws this
	 *  when present, falling back to its generated mirrored wordmark when
	 *  null (no upload, or the resolve failed). */
	readonly logoUrl: string | null;
	readonly schedule: {
		readonly start: TimestampLike;
		readonly end: TimestampLike;
	};
	readonly timezone: string;
	readonly venue: string | null;
	readonly address: string | null;
	readonly mapsUrl: string | null;
	readonly notes: string | null;
};

export type OrderAccessData =
	| { readonly status: "loading" }
	| { readonly status: "not-found" }
	| {
			readonly status: "authorised";
			readonly event: OrderAccessEventContext;
			/** Session-backed only — OrderAccess never carries buyerEmail (a
			 *  deliberate privacy boundary on a bare-capability token); null on
			 *  every cross-session, token-backed read. */
			readonly buyerEmail: string | null;
			readonly sinceMs: number;
	  }
	| {
			readonly status: "paid";
			readonly fulfillment: "issuing";
			readonly event: OrderAccessEventContext;
			readonly sinceMs: number;
	  }
	| {
			readonly status: "paid";
			readonly fulfillment: "partial" | "complete";
			readonly event: OrderAccessEventContext;
			/** The token this page is reached by — the same one the real
			 *  workspaces/{orgId}/orderAccess/{token} doc is keyed on, and the same
			 *  one redirectWalletSaveLink's per-ticket route addresses. Only
			 *  present once a real doc has resolved, which "partial"/"complete"
			 *  both guarantee. */
			readonly orderAccessToken: string;
			readonly tickets: readonly OrderAccessTicket[];
			readonly expectedCount: number | null;
			readonly artifacts: OrderAccessArtifacts | null;
	  }
	| {
			readonly status: "refunded" | "cancelled";
			readonly event: OrderAccessEventContext;
			readonly tickets: readonly OrderAccessTicket[];
			readonly receipt: ResolvedArtifactSlot;
	  };

const initial: OrderAccessData = { status: "loading" };

// keepOnClear: this page is token-bound, not uid-bound — the buyer holds no
// account, the token IS the capability — so the anonymous sign-in landing
// elsewhere in the tab (session.sync.ts answers the uid transition with
// clearStates()) must not wipe it. Its sole writer is the token's Firestore
// listener (order-access.sync.ts), and for a dead token that listener is
// denied and torn down by the SDK, never to re-emit, so a reset here would
// strand the page at the loading skeleton where not-found had already
// landed. The route's own disconnect is the reset that owns this state.
export const orderAccessState = state<OrderAccessData>(
	"storefront/order-access",
).memory(initial, { keepOnClear: true });

/** The event/venue snapshot this page is showing, or null on the two
 *  branches that name no event at all (loading, not-found) — the one answer
 *  every surface reading it shares, so a card, the sheet's composed message
 *  and the link it carries cannot describe different events. */
export const orderAccessEvent = computed<OrderAccessEventContext | null>(() => {
	const data = orderAccessState.value;
	return "event" in data ? data.event : null;
});

/** A ticket's 1-based place in its own order and the order's settled total,
 *  printed on the card as "2 / 3". It counts the ORDER, never the rail: the
 *  rail can be scoped to the tickets still with the holder, and a number that
 *  followed the rail would renumber a credential the buyer has already saved
 *  as a file. Null while a ticket is still minting — a count the next render
 *  revises is a lie — and null on a single-ticket order, where there is
 *  nothing to be one of. */
export type TicketSequence = {
	readonly index: number;
	readonly count: number;
};

/** One minted ticket as the rail holds it: the ticket, and the place its card
 *  prints. Resolved here rather than at render because two surfaces draw the
 *  same card — the rail, and the off-screen twin a save rasterizes — and a
 *  position computed twice is a position free to disagree with itself. */
export type OrderAccessTicketRow = {
	readonly kind: "ticket";
	readonly ticket: OrderAccessTicket;
	readonly sequence: TicketSequence | null;
};

/** The carousel's own row shape — a real ticket, or a placeholder row
 *  standing in for one still being minted. A distinct sentinel kind, not a
 *  fabricated ticket with an empty qrToken, since it's never a redeemable
 *  credential. */
export type OrderAccessCarouselItem =
	| OrderAccessTicketRow
	| { readonly kind: "pending" };

/** The ticket rail's render-ready row list — never empty while an order is
 *  still resolving, so the rail has the same geometry in every state and a
 *  minted ticket lands INSIDE the silhouette its placeholder already held
 *  rather than pushing the page around when it arrives. A wait is a
 *  shimmering card and nothing else: no status card, no banner, no copy that
 *  appears and disappears on a timer. Kept as a `computed` (not assembled
 *  inline in order-access.view.ts's render()) because it's a real display
 *  projection over `orderAccessState`, not a forwarded field. */
export const orderAccessCarousel = computed<readonly OrderAccessCarouselItem[]>(
	() => {
		const data = orderAccessState.value;
		// Nothing minted yet — loading, the authorised wait, the issuing wait:
		// one placeholder, because the true count isn't knowable (OrderAccess
		// carries no line-item total) and inventing a second silhouette would
		// promise a ticket this order may not have.
		if (data.status === "loading") return [{ kind: "pending" as const }];
		if (data.status === "not-found") return [];
		if (data.status === "authorised") return [{ kind: "pending" as const }];
		if (data.status === "paid" && data.fulfillment === "issuing")
			return [{ kind: "pending" as const }];

		// A ticket still minting leaves the count unsettled, so no card is
		// numbered until the last one has landed.
		const settled = !(data.status === "paid" && data.fulfillment === "partial");
		const count = data.tickets.length;
		const items: OrderAccessCarouselItem[] = data.tickets.map((ticket, i) => ({
			kind: "ticket" as const,
			ticket,
			sequence: settled && count > 1 ? { index: i + 1, count } : null,
		}));
		if (!settled) items.push({ kind: "pending" as const });
		return items;
	},
);

/** How many tickets this order holds — the widest number the rail's row ever
 *  prints, and 0 before any has minted. Every numeral on that row is written
 *  at this number's digit width, so a count that changes never changes width
 *  and never drags the one beside it sideways. */
export const orderTicketCount = computed<number>(() =>
	"tickets" in orderAccessState.value
		? orderAccessState.value.tickets.length
		: 0,
);

/** Cards asked for as image files, by ticket id — the header chip's whole
 *  order, or one card's own. Ids rather than rows, because a per-ticket
 *  control holds only its ticket and the row that carries the number printed
 *  on the card is the carousel's to resolve; resolving it at each call site
 *  would let the file and the card disagree about which of N the ticket is.
 *  order-access.view.ts is the one subscriber: it owns the off-screen twin
 *  every capture is taken from. */
export const saveTicketImages$ = new Subject<{
	readonly ticketIds: readonly string[];
	/** Null identifies the order-wide action; otherwise the clicked ticket overlay. */
	readonly sourceTicketId: string | null;
}>();

export const failedTicketImageSave = state<{
	readonly sourceTicketId: string | null;
} | null>("storefront/order-access/failed-ticket-image-save").memory(null);

/** The capture twin and the request that owns its busy feedback. The owning
 *  view clears the job on settlement or teardown; terminal refusal remains
 *  correlated to the same source so its retry stays local. */
export type TicketImageSave = {
	readonly sourceTicketId: string | null;
	readonly row: OrderAccessTicketRow;
	readonly done: number;
	readonly total: number;
};

export const savingTicketImage = state<TicketImageSave | null>(
	"storefront/order-access/saving-ticket-image",
).memory(null);

/** One artifact chip, ready to render — order-wide (.ics/receipt/Apple
 *  Wallet), never per-ticket. A `pending` chip renders labeled and dimmed
 *  rather than shimmering: several slots sit `pending` simultaneously in
 *  the ordinary seconds after payment, and dimming alone can't tell "still
 *  being prepared" from "broken", so a pending chip also swaps to the
 *  shared "in progress" icon and carries its own accessible name
 *  (order-access.view.ts) — its size and position hold identical to the
 *  `ready` chip beside it by construction; only `icon`/`href`/`pending`
 *  differ. walletGoogle carries no chip here: its resolved slot is a url
 *  list, not the one href this shape holds, and it renders through its own
 *  Google-supplied badge instead — see OrderAccessArtifacts's own doc.
 *  walletApple carries no chip here either: it renders through Apple's own
 *  supplied mark, which this shape's icon/label chrome cannot carry —
 *  apple-wallet-badge.view.ts renders it instead. */
export type ArtifactChip = {
	readonly kind: "ics" | "receipt";
	readonly icon: string;
	readonly opensNewTab: boolean;
	readonly href: string | null;
	readonly pending: boolean;
};

const ARTIFACT_CHIP_META: Record<
	ArtifactChip["kind"],
	{ readonly icon: string; readonly opensNewTab: boolean }
> = {
	ics: { icon: "calendar_add_on", opensNewTab: false },
	receipt: { icon: "receipt_long", opensNewTab: true },
};

/** The one glyph every kind swaps to while pending — the same "we're
 *  working on it" icon order-access.view.ts's own confirming/issuing
 *  waitingCard already renders, so the signal reads consistently wherever
 *  it shows up on this page rather than leaving a kind-icon (a download
 *  arrow, a wallet) to be misread as a stuck or broken action. */
const PENDING_ARTIFACT_ICON = "hourglass_top";

function toChip(
	kind: ArtifactChip["kind"],
	slot: ResolvedArtifactSlot,
): ArtifactChip | null {
	if (slot.state === "never") return null;
	const pending = slot.state === "pending";
	return {
		kind,
		icon: pending ? PENDING_ARTIFACT_ICON : ARTIFACT_CHIP_META[kind].icon,
		opensNewTab: ARTIFACT_CHIP_META[kind].opensNewTab,
		href: slot.state === "ready" ? slot.url : null,
		pending,
	};
}

/** The artifact rail's own render-ready row list — a `never` slot is
 *  excluded entirely (no chip), so order-access.view.ts's rail wrapper
 *  renders only when this array is non-empty, never an empty flex row still
 *  claiming its parent's gap. The refunded/cancelled branch reads from here
 *  too, over its own single `receipt` slot, so a refund landing before the
 *  receipt task finishes shows the same dimmed pending chip rather than
 *  nothing. walletGoogle is read straight off `data.artifacts.walletGoogle`
 *  by whatever renders the Google Wallet badge, never through here. */
export const artifactChips = computed<readonly ArtifactChip[]>(() => {
	const data = orderAccessState.value;
	if (data.status === "paid" && data.fulfillment === "complete") {
		const artifacts = data.artifacts;
		if (!artifacts) return [];
		// No chip for the ticket files — each card carries its own share action.
		return (["ics", "receipt"] as const)
			.map((kind) => toChip(kind, artifacts[kind]))
			.filter((chip): chip is ArtifactChip => chip !== null);
	}
	if (data.status === "refunded" || data.status === "cancelled") {
		const chip = toChip("receipt", data.receipt);
		return chip ? [chip] : [];
	}
	return [];
});

/** Where this order's Google Wallet passes stand — the whole three-state
 *  answer `<google-wallet-badge>` renders against, so a slot this deployment
 *  will never fill claims no space while one still being written holds the
 *  badge's own box. Every branch short of paid+complete reads `never`: no
 *  artifact state has resolved there, and reserving against an unknown is
 *  what leaves a placeholder standing over an order that has no pass coming. */
export const walletGoogleSlot = computed<ArtifactSlotState>(() => {
	const data = orderAccessState.value;
	if (data.status !== "paid" || data.fulfillment !== "complete") return "never";
	return data.artifacts?.walletGoogle.state ?? "never";
});

/** Whether this order's Google Wallet passes exist and are ready — the one
 *  term order-access.sync.ts (the one role allowed to touch the network)
 *  reads to decide whether to call mintWalletSaveLinks. */
export const walletGoogleReady = computed<boolean>(
	() => walletGoogleSlot.get() === "ready",
);

/** Whether this order carries more than one ticket — the per-ticket takeaway
 *  control's whole gate (ticket-wallet-actions.view.ts): at one ticket there
 *  is no "this one vs. all" to distinguish, so the header strip's own
 *  order-level controls already are the per-ticket surface. False
 *  for every branch short of "complete" — the only one that carries a real
 *  ticket count. */
export const walletMultiTicket = computed<boolean>(() => {
	const data = orderAccessState.value;
	if (data.status !== "paid" || data.fulfillment !== "complete") return false;
	return data.tickets.length > 1;
});

/** The most passes one wallet gesture can carry, taken as the LOWER of the
 *  two vendors' own ceilings so that one number governs both marks and
 *  neither vendor's row is ever the one that grows.
 *
 *  Google publishes no object count at all. What it publishes is a
 *  1,800-character safe length for the encoded save link, which this
 *  deployment's own signer resolves into six passes: a fixed ~633 characters
 *  of claims plus ~169 per {classId,id} pair crosses 1,800 at the seventh
 *  (mintWalletSaveLinks.api.ts's MAX_OBJECTS_PER_SAVE_LINK). Above six
 *  Google's answer is not a longer link, it is ceil(n/6) links — and
 *  ceil(n/6) identical marks standing in one row is what a buyer reads as a
 *  broken page rather than as an offer.
 *
 *  Apple's is ten to a `.pkpasses` archive; an eleventh fails the download
 *  itself, which is why fulfillment writes no bundle above it
 *  (walletApple.api.ts's APPLE_BUNDLE_PASS_CEILING).
 *
 *  Six, then. Between seven and ten Apple alone could still bundle, and that
 *  is precisely the shape declined: one vendor's mark standing where the
 *  other cannot follow reads as the other one being broken, and above the
 *  ceiling every ticket's own card already carries both vendors' passes and
 *  its own image file one guest at a time — which on an order that size is
 *  how the tickets were always going to be handed out. */
export const WALLET_BULK_PASS_CEILING = 6;

/** Whether this order holds more tickets than one order-wide gesture can
 *  carry — the single term every ORDER-LEVEL control withdraws on, wallet
 *  marks and "save every ticket" alike, so the header never offers one of
 *  them at a size where the other has already given up. Each ticket's own
 *  card is the whole surface past it. False short of paid+complete, where no
 *  ticket count exists yet. */
export const pastBulkCeiling = computed<boolean>(() => {
	const data = orderAccessState.value;
	if (data.status !== "paid" || data.fulfillment !== "complete") return false;
	return data.tickets.length > WALLET_BULK_PASS_CEILING;
});

/** Whether this order's whole set fits in one gesture per vendor — the sole
 *  term the header strip's order-level badges render on. Above the ceiling
 *  neither vendor's order-level mark renders and each ticket's own
 *  <ticket-wallet-actions> is the entire takeaway surface. False short of
 *  paid+complete, where no ticket count exists yet and both slots already
 *  read `never`. */
export const walletBulkOffered = computed<boolean>(() => {
	const data = orderAccessState.value;
	if (data.status !== "paid" || data.fulfillment !== "complete") return false;
	return !pastBulkCeiling.get();
});

/** Where this order's Google Wallet save-link mint stands. A url list alone
 *  cannot carry this: empty reads identically for a request still
 *  travelling and one that came back with nothing, and a badge that reserves
 *  its footprint against "empty" holds a skeleton forever over a pass that
 *  is never coming. Each state is a different answer to the buyer's own
 *  question — `minting` is open (reserve the box, the mark is on its way),
 *  `ready` carries the links, `failed` is closed (reserve nothing) — so the
 *  badge reads an outcome rather than inferring one from a payload's
 *  absence.
 *
 *  `failed` is terminal for the mount: order-access.sync.ts has already
 *  spent its bounded retries by the time it lands here, and the signing
 *  key's own lifetime (order-access.entity.ts's WalletGoogleSlotSchema)
 *  means a link is minted per page view anyway, so the next mount tries
 *  again from `idle`. Apple carries no counterpart — its passes are signed
 *  and stored at fulfillment, so walletAppleSlot's own token is already the
 *  whole answer and there is no client-side act left to fail. */
export type WalletGoogleMint =
	| { readonly status: "idle" }
	| { readonly status: "minting" }
	| { readonly status: "ready"; readonly urls: readonly string[] }
	| { readonly status: "failed" };

/** Reset to `idle` on disconnect (order-access.sync.ts) so a stale,
 *  possibly-unresolvable link never survives into a later mount.
 *
 *  keepOnClear, for orderAccessState's reason: the mint is token-bound and
 *  per page view, and `idle` doubles as its once-per-mount guard, so a
 *  clearStates() landing after the mint would leave the badge reserving its
 *  box against `idle` with nothing left to re-mint — or spend a second
 *  signing when the listener happens to re-emit. */
export const walletGoogleMint = state<WalletGoogleMint>(
	"storefront/order-access/wallet-google-mint",
).memory({ status: "idle" }, { keepOnClear: true });

/** This order's signed Apple Wallet passes, one {ticketId,url} pair per
 *  ticket — unlike Google's save links there is nothing to mint, Apple's
 *  passes are signed and stored at fulfillment time, so this reads straight
 *  off `data.artifacts.walletAppleUrls` (already resolved by
 *  order-access.api.ts) rather than through a `.sync`-minted side-state like
 *  walletGoogleMint. Empty for `never`/`pending` alike. The per-ticket
 *  <apple-wallet-badge> addresses one ticket's own pass through here; the
 *  order-level one reads it only as the fallback walletAppleBundleUrl's own
 *  doc describes. */
export const walletAppleUrls = computed<
	readonly { readonly ticketId: string; readonly url: string }[]
>(() => {
	const data = orderAccessState.value;
	if (data.status !== "paid" || data.fulfillment !== "complete") return [];
	return data.artifacts?.walletAppleUrls ?? [];
});

/** The one URL that adds this order's whole set of passes at once — the
 *  `.pkpasses` bundle signed and stored at fulfillment beside the individual
 *  passes, which is what lets the order-level <apple-wallet-badge> be a
 *  single control the way Google's bundled save badge already is. Gated on
 *  the same paid+complete branch walletAppleUrls reads, so a refund or
 *  cancellation withdraws both together. Null for `never`/`pending`, and
 *  null for an order fulfilled before the bundle was written — the badge
 *  falls back to that order's individual passes rather than losing the
 *  action. */
export const walletAppleBundleUrl = computed<string | null>(() => {
	const data = orderAccessState.value;
	if (data.status !== "paid" || data.fulfillment !== "complete") return null;
	return data.artifacts?.walletAppleBundleUrl ?? null;
});

/** Where this order's Apple Wallet passes stand — walletGoogleSlot's own
 *  counterpart, and the term `<apple-wallet-badge>` reserves its footprint
 *  against. A resolved url of either kind IS `ready`; short of that only the
 *  slot's own state token separates "this deployment signs no passes" from
 *  "the pass is still being signed", and a projection that carries no token
 *  reads `never` — the answer that reserves nothing, which is the only safe
 *  reading when the question itself is unavailable. */
export const walletAppleSlot = computed<ArtifactSlotState>(() => {
	const data = orderAccessState.value;
	if (data.status !== "paid" || data.fulfillment !== "complete") return "never";
	const artifacts = data.artifacts;
	if (artifacts == null) return "never";
	if (
		artifacts.walletAppleBundleUrl != null ||
		artifacts.walletAppleUrls.length > 0
	)
		return "ready";
	return artifacts.walletApple === "pending" ? "pending" : "never";
});
